import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AgentStatus, AvailableNumber } from '@/types/agents'

interface AvailableNumbersResponse {
  numbers: AvailableNumber[]
}

export interface TeamMember {
  id: number
  user_id: number
  user_email: string
  user_first_name: string
  user_last_name: string
  status: 'ONLINE' | 'OFFLINE' | 'BUSY'
  status_display?: string
  dnd_enabled?: boolean
  role: string
  company: number
}

interface TeamResponse {
  results?: TeamMember[]
  count?: number
}

export function useTeamMembers() {
  return useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const { data } = await api.get<TeamResponse | TeamMember[]>('/api/team/')
      if (Array.isArray(data)) return data
      return data.results || []
    },
    refetchInterval: 30_000, // refresh statut toutes les 30s
    staleTime: 25_000,
  })
}

export function useAvailableCallerIds() {
  return useQuery({
    queryKey: ['available-caller-ids'],
    queryFn: async () => {
      const { data } = await api.get<AvailableNumbersResponse>(
        '/api/agents/available-numbers/',
      )
      return data.numbers || []
    },
  })
}

// ── Sticky du téléphone manuel (mode Automatique), par agent ──────────────
// Mémorise le dernier caller ID présenté à un contact. Distinct de la sticky
// campagne (StickyCallerId). Clé : numéro du contact normalisé (E.164).

export function softphoneStickyKey(contactPhone: string | null) {
  return ['softphone-sticky', contactPhone || ''] as const
}

/** Récupère le dernier numéro utilisé pour `contactPhone` (null si aucun). */
export function useSoftphoneSticky(contactPhone: string | null) {
  return useQuery({
    queryKey: softphoneStickyKey(contactPhone),
    enabled: !!contactPhone,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.get<{ caller_id: string | null }>(
        '/api/agents/softphone/sticky/',
        { params: { contact_phone: contactPhone } },
      )
      return data.caller_id
    },
  })
}

/** Mémorise le numéro présenté à un contact (appelé après un appel en auto). */
export function useSaveSoftphoneSticky() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      contact_phone: string
      caller_id: string
    }) => {
      const { data } = await api.post<{ caller_id: string }>(
        '/api/agents/softphone/sticky/',
        payload,
      )
      return data.caller_id
    },
    onSuccess: (callerId, variables) => {
      qc.setQueryData(softphoneStickyKey(variables.contact_phone), callerId)
    },
  })
}

/**
 * The sidebar dot. The server derives `status` from Do not disturb, the one
 * setting the web, the iPhone and Android all change: OFFLINE means Do not
 * disturb. Kept live by the `ne_pas_deranger` event (useSSEHandlers).
 */
export function useAgentStatus() {
  return useQuery({
    queryKey: ['agent-status'],
    queryFn: async () => {
      const { data } = await api.get<{ status: AgentStatus }>(
        '/api/agents/status/get/',
      )
      return data.status
    },
  })
}

export function useToggleAgentStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (current: AgentStatus) => {
      const next: AgentStatus = current === 'ONLINE' ? 'OFFLINE' : 'ONLINE'
      // The server sets Do not disturb along with the status, in one write.
      const { data } = await api.post<{ status: AgentStatus }>('/api/agents/status/', {
        status: next,
      })
      return data.status
    },
    onSuccess: (status) => {
      queryClient.setQueryData(['agent-status'], status)
    },
  })
}
